import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X, ImageOff, Check, PackageCheck, HandCoins, Ban, ExternalLink, RotateCcw } from "lucide-react";
import {
  ShopOrder,
  ShopOrderStatus,
  ShopPaymentStatus,
  getAdminShopOrders,
  getShopOrder,
  approveShopOrderPayment,
  rejectShopOrderPayment,
  markShopOrderReady,
  collectShopOrder,
  cancelShopOrder,
  markShopOrderRefunded,
} from "../../../api/shop";
import { useAppDispatch } from "../../../store/store";
import { setGeneralAlertMessage } from "../../../store/slices/generalSlice";
import {
  formatAmount,
  formatEGP,
  fmtCairoDateTime,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_TONE,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_TONE,
} from "../shopFormat";
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  DescriptionList,
  Dialog,
  EmptyState,
  Field,
  Loader,
  PageHeader,
  SearchInput,
  Select,
  TableWrap,
  Tag,
  Textarea,
  Toolbar,
} from "../../../ui";
import type { DescriptionItem } from "../../../ui";
import "../styles.scss";
import "./styles.scss";

const STATUS_OPTIONS = Object.keys(ORDER_STATUS_LABELS) as ShopOrderStatus[];
const PAYMENT_STATUS_OPTIONS = Object.keys(PAYMENT_STATUS_LABELS) as ShopPaymentStatus[];

const itemCount = (order: ShopOrder) => order.lines.reduce((sum, l) => sum + l.quantity, 0);

const StatusPill = ({ order }: { order: ShopOrder }) => (
  <Tag tone={ORDER_STATUS_TONE[order.status] || "neutral"}>{ORDER_STATUS_LABELS[order.status] || order.status}</Tag>
);

const PaymentPill = ({ order }: { order: ShopOrder }) => (
  <Tag tone={PAYMENT_STATUS_TONE[order.paymentStatus] || "neutral"}>
    {PAYMENT_STATUS_LABELS[order.paymentStatus] || order.paymentStatus}
  </Tag>
);

// Ordered without an account
const GuestTag = () => (
  <Tag tone="neutral" className="guest-pill">
    Guest
  </Tag>
);

// What the order's state allows (mirrors the backend rules).
const allowedActions = (o: ShopOrder) => {
  const open = o.status !== "Collected" && o.status !== "Cancelled";
  const reviewable = o.status === "Placed" && o.paymentMethod === "InstaPay" && o.paymentStatus === "AwaitingReview";
  const cashUnpaid = o.paymentMethod === "Cash" && o.paymentStatus === "Unpaid";
  return {
    approve: reviewable,
    reject: reviewable,
    ready: o.status === "Placed" && (o.paymentStatus === "Paid" || cashUnpaid),
    collect: (o.status === "Placed" || o.status === "ReadyForPickup") && (o.paymentStatus === "Paid" || cashUnpaid),
    collectCash: cashUnpaid,
    // While a receipt is under review, "Reject payment" is the cancel.
    cancel: open && !reviewable,
    // A cancelled paid guest order: the money goes back by hand, then staff record it here.
    markRefunded: o.paymentStatus === "RefundDue",
  };
};

type ConfirmKind = "reject" | "cancel" | "collectCash" | "markRefunded";

const ShopOrders = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = (searchParams.get("status") || "") as ShopOrderStatus | "";
  const paymentFilter = (searchParams.get("paymentStatus") || "") as ShopPaymentStatus | "";
  const selectedId = searchParams.get("order");

  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
  const [search, setSearch] = useState(searchInput);
  const [orders, setOrders] = useState<ShopOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ShopOrder | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmKind | null>(null);
  const [reason, setReason] = useState("");
  const requestSeq = useRef(0);

  const setParam = useCallback(
    (key: string, value: string) =>
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (value) p.set(key, value);
          else p.delete(key);
          return p;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  // Debounce typing into the search box.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setParam("search", search);
  }, [search, setParam]);

  const fetchOrders = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await getAdminShopOrders({ status: statusFilter, paymentStatus: paymentFilter, search });
      if (seq !== requestSeq.current) return;
      setOrders(Array.isArray(res?.data) ? res.data : []);
    } catch (err: any) {
      if (seq !== requestSeq.current) return;
      console.error("Error fetching shop orders", err);
      setOrders([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [statusFilter, paymentFilter, search]);

  useEffect(() => {
    document.title = "Shop Orders";
    fetchOrders();
  }, [fetchOrders]);

  // Keep the drawer in sync with ?order=… (so a refresh or shared link reopens it).
  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }
    const fromList = orders.find((o) => o.id === selectedId);
    if (fromList) setSelected(fromList);
    let cancelled = false;
    getShopOrder(selectedId)
      .then((res) => {
        if (!cancelled && res?.data) setSelected(res.data);
      })
      .catch((err: any) => {
        if (cancelled || fromList) return;
        dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Order not found" }));
        setParam("order", "");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const openOrder = (order: ShopOrder) => {
    setSelected(order);
    setParam("order", order.id);
  };

  const closeDrawer = useCallback(() => {
    if (busy) return;
    setConfirm(null);
    setParam("order", "");
  }, [busy, setParam]);

  const applyUpdated = (updated: ShopOrder) => {
    setSelected(updated);
    setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
  };

  const runAction = async (action: () => Promise<{ data: ShopOrder }>, success: string, fallback: string) => {
    setBusy(true);
    try {
      const res = await action();
      if (res?.data) applyUpdated(res.data);
      dispatch(setGeneralAlertMessage({ type: "success", message: success }));
      setConfirm(null);
      setReason("");
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || fallback }));
    } finally {
      setBusy(false);
    }
  };

  const openConfirm = (kind: ConfirmKind) => {
    setReason("");
    setConfirm(kind);
  };

  const handleConfirm = () => {
    if (!selected || !confirm) return;
    const id = selected.id;
    const n = selected.orderNumber;
    if (confirm === "reject") {
      runAction(() => rejectShopOrderPayment(id, reason), `Payment rejected · order #${n} cancelled`, "Failed to reject payment");
    } else if (confirm === "cancel") {
      runAction(() => cancelShopOrder(id, reason), `Order #${n} cancelled`, "Failed to cancel order");
    } else if (confirm === "markRefunded") {
      runAction(() => markShopOrderRefunded(id), `Order #${n} marked as refunded`, "Failed to mark order refunded");
    } else {
      runAction(() => collectShopOrder(id), `Order #${n} collected`, "Failed to collect order");
    }
  };

  const hasFilters = !!(statusFilter || paymentFilter || searchInput);

  // The drawer and its confirm keep showing the last order / question while they animate closed.
  const lastSelected = useRef<ShopOrder | null>(null);
  if (selected) lastSelected.current = selected;
  const drawerOrder = selected ?? lastSelected.current;
  const actions = drawerOrder ? allowedActions(drawerOrder) : null;
  const lastConfirm = useRef<ConfirmKind>("cancel");
  if (confirm) lastConfirm.current = confirm;
  const confirmKind = confirm ?? lastConfirm.current;

  const detailItems = (items: (DescriptionItem | false | null | undefined)[]) =>
    items.filter((i): i is DescriptionItem => !!i);

  return (
    <div className="shop-page shop-orders-page">
      <PageHeader
        title="Orders"
        subtitle={`${orders.length} ${orders.length === 1 ? "Order" : "Orders"} · collected at The Clash`}
      />

      <Toolbar>
        <SearchInput
          className="orders-search"
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Search order #, name, email, phone or SKU"
        />
        <Select
          className={`filter-select ${statusFilter ? "has-value" : ""}`}
          aria-label="Status"
          value={statusFilter}
          onChange={(e) => setParam("status", e.target.value)}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {ORDER_STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
        <Select
          className={`filter-select ${paymentFilter ? "has-value" : ""}`}
          aria-label="Payment"
          value={paymentFilter}
          onChange={(e) => setParam("paymentStatus", e.target.value)}
        >
          <option value="">All payments</option>
          {PAYMENT_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {PAYMENT_STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
        {hasFilters && (
          <Button
            variant="ghost"
            icon={<X size={14} />}
            onClick={() => {
              setSearchInput("");
              setSearchParams(
                (prev) => {
                  const p = new URLSearchParams(prev);
                  p.delete("status");
                  p.delete("paymentStatus");
                  p.delete("search");
                  return p;
                },
                { replace: true }
              );
            }}
          >
            Reset
          </Button>
        )}
      </Toolbar>

      {loading && orders.length === 0 ? (
        <Loader />
      ) : orders.length === 0 ? (
        <Card padding="none">
          <EmptyState title={hasFilters ? "No orders match these filters." : "No orders yet."} />
        </Card>
      ) : (
        <>
          <TableWrap className={`shop-table-wrap ${loading ? "is-refreshing" : ""}`}>
            <table className="ui-table shop-table orders-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th className="ui-table__num">Total</th>
                  <th>Method</th>
                  <th>Payment</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr
                    key={order.id}
                    className={`is-clickable ${selected?.id === order.id ? "is-selected" : ""}`}
                    onClick={() => openOrder(order)}
                  >
                    <td className="order-number">#{order.orderNumber}</td>
                    <td className="nowrap date-cell">{fmtCairoDateTime(order.createdAt)}</td>
                    <td>
                      <div className="customer-cell">
                        <span className="customer-name">
                          {order.customer?.name || "-"}
                          {order.isGuest && <GuestTag />}
                        </span>
                        {(order.customer?.phone || order.customer?.email) && (
                          <span className="customer-contact">{order.customer.phone || order.customer.email}</span>
                        )}
                      </div>
                    </td>
                    <td className="nowrap">
                      {itemCount(order)} {itemCount(order) === 1 ? "item" : "items"}
                    </td>
                    <td className="nowrap amount-cell ui-table__num">{formatEGP(order.total)}</td>
                    <td className="nowrap">{PAYMENT_METHOD_LABELS[order.paymentMethod] || order.paymentMethod}</td>
                    <td>
                      <PaymentPill order={order} />
                    </td>
                    <td>
                      <StatusPill order={order} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>

          {/* Phones: the same rows as cards (the table above is hidden under 768px); a tap opens the same drawer */}
          <div className={`shop-cards ${loading ? "is-refreshing" : ""}`}>
            {orders.map((order) => (
              <Card
                key={order.id}
                padding="sm"
                className={`shop-card order-card ${selected?.id === order.id ? "is-selected" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => openOrder(order)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openOrder(order);
                  }
                }}
              >
                <div className="order-card__top">
                  <span className="order-number">#{order.orderNumber}</span>
                  <StatusPill order={order} />
                </div>
                <div className="order-card__customer">
                  <span className="customer-name">
                    {order.customer?.name || "-"}
                    {order.isGuest && <GuestTag />}
                  </span>
                  {(order.customer?.phone || order.customer?.email) && (
                    <span className="customer-contact">{order.customer.phone || order.customer.email}</span>
                  )}
                </div>
                <span className="order-card__date">{fmtCairoDateTime(order.createdAt)}</span>
                <div className="order-card__bottom">
                  <span className="order-card__total">
                    {formatEGP(order.total)}
                    <span className="order-card__items">
                      {itemCount(order)} {itemCount(order) === 1 ? "item" : "items"}
                    </span>
                  </span>
                  <span className="order-card__payment">
                    <span className="order-card__method">
                      {PAYMENT_METHOD_LABELS[order.paymentMethod] || order.paymentMethod}
                    </span>
                    <PaymentPill order={order} />
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Detail drawer */}
      {drawerOrder && actions && (
        <Dialog
          open={!!selected}
          onClose={closeDrawer}
          placement="side"
          size="md"
          busy={busy}
          className="order-drawer"
          title={
            <>
              <span className="drawer-eyebrow">Shop order</span>#{drawerOrder.orderNumber}
            </>
          }
          subtitle={
            <>
              Placed {fmtCairoDateTime(drawerOrder.createdAt)}
              <span className="drawer-pills">
                <StatusPill order={drawerOrder} />
                <PaymentPill order={drawerOrder} />
              </span>
            </>
          }
          footer={
            actions.approve || actions.reject || actions.ready || actions.collect || actions.cancel || actions.markRefunded ? (
              <>
                {actions.approve && (
                  <Button
                    icon={<Check size={14} />}
                    disabled={busy}
                    onClick={() =>
                      runAction(
                        () => approveShopOrderPayment(drawerOrder.id),
                        `Payment approved for order #${drawerOrder.orderNumber}`,
                        "Failed to approve payment"
                      )
                    }
                  >
                    Approve payment
                  </Button>
                )}
                {actions.reject && (
                  <Button variant="danger-tint" icon={<X size={14} />} disabled={busy} onClick={() => openConfirm("reject")}>
                    Reject payment
                  </Button>
                )}
                {actions.ready && (
                  <Button
                    icon={<PackageCheck size={14} />}
                    disabled={busy}
                    onClick={() =>
                      runAction(
                        () => markShopOrderReady(drawerOrder.id),
                        `Order #${drawerOrder.orderNumber} is ready for pickup`,
                        "Failed to mark order ready"
                      )
                    }
                  >
                    Mark ready
                  </Button>
                )}
                {actions.collect && (
                  <Button
                    variant={actions.ready ? "tint" : "primary"}
                    icon={<HandCoins size={14} />}
                    disabled={busy}
                    onClick={() =>
                      actions.collectCash
                        ? openConfirm("collectCash")
                        : runAction(
                            () => collectShopOrder(drawerOrder.id),
                            `Order #${drawerOrder.orderNumber} collected`,
                            "Failed to collect order"
                          )
                    }
                  >
                    {actions.collectCash ? "Collect & mark cash paid" : "Mark collected"}
                  </Button>
                )}
                {actions.markRefunded && (
                  <Button icon={<RotateCcw size={14} />} disabled={busy} onClick={() => openConfirm("markRefunded")}>
                    Mark refunded
                  </Button>
                )}
                {actions.cancel && (
                  <Button variant="danger-tint" icon={<Ban size={14} />} disabled={busy} onClick={() => openConfirm("cancel")}>
                    Cancel order
                  </Button>
                )}
              </>
            ) : undefined
          }
        >
          {drawerOrder.status === "Cancelled" && drawerOrder.cancelReason && (
            <Alert tone="danger" title="Cancel reason" className="cancel-reason">
              {drawerOrder.cancelReason}
            </Alert>
          )}

          <section className="drawer-section">
            <p className="drawer-section-label">Items</p>
            <div className="order-lines">
              {drawerOrder.lines.map((line, i) => (
                <div key={`${line.variantId || line.productName}-${i}`} className="order-line">
                  <div className="product-thumb small">
                    {line.imageUrl ? <img src={line.imageUrl} alt={line.productName} /> : <ImageOff size={14} />}
                  </div>
                  <div className="order-line-info">
                    <span className="order-line-name">{line.productName}</span>
                    <span className="order-line-meta">
                      {line.variantLabel || line.sizeLabel} · {line.quantity} × {formatEGP(line.unitPrice)}
                    </span>
                    {line.sku && <span className="order-line-sku mono">SKU {line.sku}</span>}
                  </div>
                  <span className="order-line-total">{formatEGP(line.lineTotal)}</span>
                </div>
              ))}
              <div className="order-total-row">
                <span>Total</span>
                <span>{formatEGP(drawerOrder.total)}</span>
              </div>
            </div>
          </section>

          <section className="drawer-section">
            <p className="drawer-section-label">Customer</p>
            <DescriptionList
              items={[
                {
                  label: "Name",
                  value: (
                    <>
                      {drawerOrder.customer?.name || "-"}
                      {drawerOrder.isGuest && <GuestTag />}
                    </>
                  ),
                },
                {
                  label: "Phone",
                  value: drawerOrder.customer?.phone ? (
                    <a href={`tel:${drawerOrder.customer.phone}`}>{drawerOrder.customer.phone}</a>
                  ) : (
                    "-"
                  ),
                },
                {
                  label: "Email",
                  wide: true,
                  value: drawerOrder.customer?.email ? (
                    <a href={`mailto:${drawerOrder.customer.email}`}>{drawerOrder.customer.email}</a>
                  ) : (
                    "-"
                  ),
                },
              ]}
            />
          </section>

          <section className="drawer-section">
            <p className="drawer-section-label">Payment</p>
            <DescriptionList
              items={detailItems([
                {
                  label: "Method",
                  value:
                    drawerOrder.paymentMethod === "Cash"
                      ? "Cash at pickup"
                      : PAYMENT_METHOD_LABELS[drawerOrder.paymentMethod] || drawerOrder.paymentMethod,
                },
                { label: "Status", value: <PaymentPill order={drawerOrder} /> },
                { label: "Amount", value: formatEGP(drawerOrder.total) },
                !!drawerOrder.merchantRefNumber && {
                  label: "Fawry ref",
                  value: <span className="mono">{drawerOrder.merchantRefNumber}</span>,
                },
              ])}
            />
            {drawerOrder.instaPayReceiptUrl && (
              <a
                className="receipt-preview"
                href={drawerOrder.instaPayReceiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-tooltip="Open receipt in a new tab"
              >
                <img src={drawerOrder.instaPayReceiptUrl} alt="InstaPay receipt" />
                <span className="receipt-open">
                  <ExternalLink size={12} /> Open receipt
                </span>
              </a>
            )}
          </section>

          {drawerOrder.notes && (
            <section className="drawer-section">
              <p className="drawer-section-label">Note from player</p>
              <p className="order-note">{drawerOrder.notes}</p>
            </section>
          )}

          <section className="drawer-section">
            <p className="drawer-section-label">Timeline</p>
            <DescriptionList
              items={detailItems([
                { label: "Placed", value: fmtCairoDateTime(drawerOrder.createdAt) },
                !!drawerOrder.paidAt && { label: "Paid", value: fmtCairoDateTime(drawerOrder.paidAt) },
                !!drawerOrder.readyAt && { label: "Ready", value: fmtCairoDateTime(drawerOrder.readyAt) },
                !!drawerOrder.collectedAt && { label: "Collected", value: fmtCairoDateTime(drawerOrder.collectedAt) },
                !!drawerOrder.cancelledAt && { label: "Cancelled", value: fmtCairoDateTime(drawerOrder.cancelledAt) },
              ])}
            />
          </section>
        </Dialog>
      )}

      {/* Confirm dialogs */}
      {drawerOrder && (
        <ConfirmDialog
          open={!!(selected && confirm)}
          onClose={(confirmed) => {
            // A successful action clears it itself; a failed one stays open, as before
            if (!confirmed) setConfirm(null);
          }}
          onConfirm={handleConfirm}
          loading={busy}
          tone={confirmKind === "collectCash" || confirmKind === "markRefunded" ? "primary" : "danger"}
          cancelLabel="Back"
          confirmLabel={
            confirmKind === "collectCash"
              ? "Collect & mark paid"
              : confirmKind === "markRefunded"
              ? "Mark refunded"
              : confirmKind === "reject"
              ? "Reject payment"
              : "Cancel order"
          }
          title={
            confirmKind === "collectCash"
              ? `Collect order #${drawerOrder.orderNumber}`
              : confirmKind === "markRefunded"
              ? `Mark order #${drawerOrder.orderNumber} refunded`
              : confirmKind === "reject"
              ? "Reject payment"
              : `Cancel order #${drawerOrder.orderNumber}`
          }
          message={
            confirmKind === "collectCash" ? (
              <p>
                Take <strong>{formatEGP(drawerOrder.total)}</strong> in cash from{" "}
                <strong>{drawerOrder.customer?.name || "the player"}</strong> and hand over the order. It will be marked
                as paid and collected.
              </p>
            ) : confirmKind === "markRefunded" ? (
              <p>
                Confirm that <strong>{formatEGP(drawerOrder.total)}</strong> has been sent back to{" "}
                <strong>{drawerOrder.customer?.name || "the guest"}</strong>
                {drawerOrder.customer?.phone ? ` (${drawerOrder.customer.phone})` : ""}.
              </p>
            ) : confirmKind === "reject" ? (
              <p>
                The InstaPay receipt for order <strong>#{drawerOrder.orderNumber}</strong> will be rejected and the order
                cancelled. Its items go back into stock.
              </p>
            ) : (
              <p>The order will be cancelled and its items go back into stock.</p>
            )
          }
        >
          {confirmKind === "cancel" &&
            drawerOrder.paymentStatus === "Paid" &&
            (drawerOrder.isGuest ? (
              <Alert tone="warn" className="refund-note">
                This guest has no wallet. The order will be marked <strong>Refund due</strong>: send EGP{" "}
                {formatAmount(drawerOrder.total)} back to them yourself, then press Mark refunded.
              </Alert>
            ) : (
              <Alert tone="warn" className="refund-note">
                EGP {formatAmount(drawerOrder.total)} will be returned to the player's wallet.
              </Alert>
            ))}

          {(confirmKind === "reject" || confirmKind === "cancel") && (
            <Field label="Reason (optional, shared with the player)" className="confirm-reason">
              {(fieldId) => (
                <Textarea
                  id={fieldId}
                  value={reason}
                  maxLength={500}
                  rows={3}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={confirmKind === "reject" ? "e.g. The receipt amount doesn't match" : "e.g. Out of stock"}
                />
              )}
            </Field>
          )}
        </ConfirmDialog>
      )}
    </div>
  );
};

export default ShopOrders;
